<script lang="ts">
	import { pageContent, type PageContent } from '$lib/seo/page-content';

	// `slug` selects which content block to render. If there's no entry for the
	// slug, the component renders nothing — safe to drop onto any page.
	let { slug }: { slug: string } = $props();

	const content: PageContent | undefined = pageContent[slug];

	// FAQPage structured data → makes the FAQ eligible for Google rich results and
	// easier for answer engines (ChatGPT/Perplexity) to parse. We escape every '<'
	// so a stray closing-script sequence in the copy can never break out of the tag.
	const faqJsonLd =
		content && content.faq?.length
			? JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'FAQPage',
					mainEntity: content.faq.map((item) => ({
						'@type': 'Question',
						name: item.q,
						acceptedAnswer: { '@type': 'Answer', text: item.a }
					}))
				}).replace(/</g, '\\u003c')
			: '';
</script>

<svelte:head>
	{#if faqJsonLd}
		{@html `<script type="application/ld+json">${faqJsonLd}</` + `script>`}
	{/if}
</svelte:head>

{#if content}
	<section class="seo-content" aria-labelledby="seo-content-heading">
		<div class="container">
			<div class="seo-content__inner">
				<h2 id="seo-content-heading" class="seo-content__heading">{content.heading}</h2>
				<p class="seo-content__intro">{content.intro}</p>

				{#each content.body as paragraph}
					<p class="seo-content__paragraph">{paragraph}</p>
				{/each}

				{#if content.faq && content.faq.length}
					<h2 class="seo-content__faq-heading">Frequently asked questions</h2>
					<div class="seo-content__faq">
						{#each content.faq as item}
							<details class="seo-content__faq-item">
								<summary class="seo-content__faq-q">{item.q}</summary>
								<p class="seo-content__faq-a">{item.a}</p>
							</details>
						{/each}
					</div>
				{/if}
			</div>
		</div>
	</section>
{/if}

<style>
	.seo-content {
		padding: var(--space-20) 0;
	}

	.seo-content__inner {
		max-width: 760px;
		margin: 0 auto;
	}

	.seo-content__heading {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.seo-content__intro {
		font-size: var(--font-size-lg);
		color: var(--text-primary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-6);
	}

	.seo-content__paragraph {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.7;
		margin-bottom: var(--space-5);
	}

	.seo-content__faq-heading {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: var(--space-12) 0 var(--space-6);
		letter-spacing: -0.02em;
	}

	.seo-content__faq {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.seo-content__faq-item {
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		padding: var(--space-4) var(--space-5);
	}

	.seo-content__faq-q {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		cursor: pointer;
	}

	.seo-content__faq-a {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.7;
		margin-top: var(--space-3);
	}
</style>
